import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AutoScrollService {
  private scrollActive = new BehaviorSubject<boolean>(false);
  private scrollRate = 0.5;
  private frameId: number | null = null;
  private paused = false;
  private pauseTimer: ReturnType<typeof setTimeout> | null = null;
  private mouseMoveListener: ((e: MouseEvent) => void) | null = null;
  private touchMoveListener: ((e: TouchEvent) => void) | null = null;
  private wheelListener: ((e: WheelEvent) => void) | null = null;

  scrollActive$ = this.scrollActive.asObservable();

  beginScroll(rate: number = 0.5): void {
    this.scrollRate = rate;
    this.paused = false;
    this.scrollActive.next(true);
    this.addUserInteractionListeners();
    this.executeScroll();
  }

  endScroll(): void {
    this.paused = true;
    this.scrollActive.next(false);
    if (this.frameId !== null) {
      cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
    this.removeUserInteractionListeners();
  }

  pauseForDuration(ms: number = 3000): void {
    this.paused = true;
    
    if (this.pauseTimer) {
      clearTimeout(this.pauseTimer);
    }
    
    this.pauseTimer = setTimeout(() => {
      this.paused = false;
      if (this.scrollActive.value) {
        this.executeScroll();
      }
    }, ms);
  }

  private addUserInteractionListeners(): void {
    // Listener pour le mouvement de la souris
    this.mouseMoveListener = () => {
      if (this.scrollActive.value && !this.paused) {
        this.pauseForDuration(3000); // Pause pendant 3 secondes après mouvement
      }
    };

    // Listener pour le touch (mobile)
    this.touchMoveListener = () => {
      if (this.scrollActive.value && !this.paused) {
        this.pauseForDuration(3000);
      }
    };

    // Listener pour la molette de la souris
    this.wheelListener = () => {
      if (this.scrollActive.value && !this.paused) {
        this.pauseForDuration(3000);
      }
    };

    window.addEventListener('mousemove', this.mouseMoveListener, { passive: true });
    window.addEventListener('touchmove', this.touchMoveListener, { passive: true });
    window.addEventListener('wheel', this.wheelListener, { passive: true });
  }

  private removeUserInteractionListeners(): void {
    if (this.mouseMoveListener) {
      window.removeEventListener('mousemove', this.mouseMoveListener);
      this.mouseMoveListener = null;
    }
    if (this.touchMoveListener) {
      window.removeEventListener('touchmove', this.touchMoveListener);
      this.touchMoveListener = null;
    }
    if (this.wheelListener) {
      window.removeEventListener('wheel', this.wheelListener);
      this.wheelListener = null;
    }
  }

  private executeScroll(): void {
    if (this.paused) return;

    const docHeight = document.documentElement.scrollHeight;
    const viewHeight = window.innerHeight;
    const maxScrollPos = docHeight - viewHeight;
    const currentPos = window.scrollY;

    if (currentPos < maxScrollPos) {
      window.scrollBy(0, this.scrollRate);
      this.frameId = requestAnimationFrame(() => this.executeScroll());
    } else {
      this.endScroll();
    }
  }

  toggle(): void {
    if (this.scrollActive.value) {
      this.endScroll();
    } else {
      this.beginScroll(this.scrollRate);
    }
  }

  updateRate(newRate: number): void {
    this.scrollRate = newRate;
  }
}